import Song, { SongAttributes } from '@interfaces/song.interface';

// A church service in the library: a sermon or gathering at one campus.
// Mirrors Sanctuary's ChurchService.
export interface ChurchService {
  id: string;
  churchID: string;
  attributes: ChurchServiceAttributes;
}

export interface ChurchServiceAttributes {
  campusID: string;
  // Null for the campus's uncategorized services.
  categoryID: string | null;
  seriesID: string | null;
  songs: Song[];
  // Localized fields arrive resolved to the caller's language, with every
  // translation alongside in `<field>Localizations`.
  title: string;
  titleLocalizations?: LocalizedTextMap;
  description: string | null;
  descriptionLocalizations?: LocalizedTextMap;
  discussionQuestions: string | null;
  discussionQuestionsLocalizations?: LocalizedTextMap;
  thumbnailURL: string | null;
  // A BlurHash of the thumbnail, for apps to show while it loads. Absent for
  // thumbnails uploaded before these were made.
  thumbnailBlurHash?: string | null;
  // Deprecated in favor of `media`. Still sent, and kept in step with the
  // service's YouTube link, for app versions that only read this.
  videoURL: string | null;
  // Where the service can be watched or listened to, one entry per platform.
  // Absent or empty for services saved before media existed.
  media?: ServiceMedia[];
  verses: BibleVerse[];
  // ISO 8601. Not public until this has passed.
  timestamp: string;
  additionalTimestamps: string[];
}

// One page of a campus's services, newest first.
export interface ChurchServicePage {
  results: ChurchService[];
  hasMore: boolean;
  // Pass back as `lastServiceID` for the next page. Null on the last one.
  nextLastServiceID: string | null;
}

// One place to watch or listen to a service: its episode or video on some
// platform. Mirrors Sanctuary's ServiceMedia.
export interface ServiceMedia {
  kind: ServiceMediaKind;
  // One of MEDIA_PLATFORMS, or a platform this portal doesn't know yet, which
  // still round-trips untouched.
  platform: string;
  // Sanctuary only accepts HTTPS.
  url: string;
  // Set on entries an import added, which a re-import may replace; null for
  // ones staff entered. Sent back exactly as received.
  origin: ServiceMediaOrigin | null;
}

export type ServiceMediaKind = 'audio' | 'video';

export interface ServiceMediaOrigin {
  importer: string;
  externalID: string | null;
  importedAt: string;
}

// The platforms apps have icons for, by the kind of media they're offered
// for. A service can list each platform once per kind.
export const MEDIA_KINDS: { id: ServiceMediaKind; name: string; platforms: { id: string; name: string }[] }[] = [
  {
    id: 'video',
    name: 'Video',
    platforms: [
      { id: 'youTube', name: 'YouTube' }
    ]
  },
  {
    id: 'audio',
    name: 'Audio',
    platforms: [
      { id: 'applePodcasts', name: 'Apple Podcasts' },
      { id: 'spotify', name: 'Spotify' },
      { id: 'amazonMusic', name: 'Amazon Music' },
      { id: 'iHeartRadio', name: 'iHeartRadio' },
      // Sanctuary's `youTube` platform as audio, which is YouTube Music.
      { id: 'youTube', name: 'YouTube Music' }
    ]
  }
];

// Most media a service can carry, per Sanctuary.
export const MAX_SERVICE_MEDIA = 20;

// A church-authored string by language, e.g. { en: 'Hope', es: 'Esperanza' }.
// `und` holds text written before languages were recorded.
export type LocalizedTextMap = Record<string, string>;

// How a localized field is sent back: a plain string, or the full map when
// other translations need to survive the save.
export type LocalizedTextValue = string | LocalizedTextMap;

export interface BibleVerse {
  book: BibleBook;
  chapter: number;
  verse: number;
  // The translation to reference, or null for none in particular.
  version?: string | null;
  // A Bible Gateway link, computed by the API.
  url?: string | null;
}

// Sanctuary's BibleBook cases, which are also their wire values, in canon
// order, with the names shown for them.
export const BIBLE_BOOKS = [
  ['genesis', 'Genesis'], ['exodus', 'Exodus'], ['leviticus', 'Leviticus'], ['numbers', 'Numbers'],
  ['deuteronomy', 'Deuteronomy'], ['joshua', 'Joshua'], ['judges', 'Judges'], ['ruth', 'Ruth'],
  ['firstSamuel', '1 Samuel'], ['secondSamuel', '2 Samuel'], ['firstKings', '1 Kings'], ['secondKings', '2 Kings'],
  ['firstChronicles', '1 Chronicles'], ['secondChronicles', '2 Chronicles'], ['ezra', 'Ezra'],
  ['nehemiah', 'Nehemiah'], ['esther', 'Esther'], ['job', 'Job'], ['psalms', 'Psalms'], ['proverbs', 'Proverbs'],
  ['ecclesiastes', 'Ecclesiastes'], ['songOfSongs', 'Song of Songs'], ['isaiah', 'Isaiah'],
  ['jeremiah', 'Jeremiah'], ['lamentations', 'Lamentations'], ['ezekiel', 'Ezekiel'], ['daniel', 'Daniel'],
  ['hosea', 'Hosea'], ['joel', 'Joel'], ['amos', 'Amos'], ['obadiah', 'Obadiah'], ['jonah', 'Jonah'],
  ['micah', 'Micah'], ['nahum', 'Nahum'], ['habakkuk', 'Habakkuk'], ['zephaniah', 'Zephaniah'],
  ['haggai', 'Haggai'], ['zechariah', 'Zechariah'], ['malachi', 'Malachi'], ['matthew', 'Matthew'],
  ['mark', 'Mark'], ['luke', 'Luke'], ['john', 'John'], ['acts', 'Acts'], ['romans', 'Romans'],
  ['firstCorinthians', '1 Corinthians'], ['secondCorinthians', '2 Corinthians'], ['galatians', 'Galatians'],
  ['ephesians', 'Ephesians'], ['philippians', 'Philippians'], ['colossians', 'Colossians'],
  ['firstThessalonians', '1 Thessalonians'], ['secondThessalonians', '2 Thessalonians'],
  ['firstTimothy', '1 Timothy'], ['secondTimothy', '2 Timothy'], ['titus', 'Titus'], ['philemon', 'Philemon'],
  ['hebrews', 'Hebrews'], ['james', 'James'], ['firstPeter', '1 Peter'], ['secondPeter', '2 Peter'],
  ['firstJohn', '1 John'], ['secondJohn', '2 John'], ['thirdJohn', '3 John'], ['jude', 'Jude'],
  ['revelation', 'Revelation']
] as const;

export type BibleBook = typeof BIBLE_BOOKS[number][0];

const bibleBookNames = new Map<string, string>(BIBLE_BOOKS);

export function bibleBookName(book: string) {
  return bibleBookNames.get(book) ?? book;
}

// e.g. "John 3:16 (ESV)".
export function formatBibleVerse(verse: BibleVerse) {
  const reference = `${bibleBookName(verse.book)} ${verse.chapter}:${verse.verse}`;

  return verse.version ? `${reference} (${verse.version})` : reference;
}

// A song as the editor sends it back. The API stores songs as given, so one
// just picked is given its ID here.
export type ChurchServiceSongUpdate = {
  id: string;
  globalID?: string | null;
  attributes: SongAttributes;
};

// The body Sanctuary's update route takes: a service's complete attributes,
// replacing what's stored. `campusID` is where the service is filed
// afterwards, moving it if it was elsewhere.
export type ChurchServiceAttributesBody = {
  campusID: string;
  categoryID: string | null;
  seriesID: string | null;
  songs: ChurchServiceSongUpdate[];
  title: LocalizedTextValue;
  description: LocalizedTextValue | null;
  discussionQuestions: LocalizedTextValue | null;
  thumbnailURL: string | null;
  thumbnailBlurHash: string | null;
  videoURL: string | null;
  media: ServiceMedia[];
  verses: BibleVerse[];
  timestamp: string;
  additionalTimestamps: string[];
};

// The fields a service's editor changes, as they'd be sent back.
export type ChurchServiceUpdate = { songs: ChurchServiceSongUpdate[]; media: ServiceMedia[] } & Pick<ChurchServiceAttributes,
  'campusID' |
  'categoryID' |
  'seriesID' |
  'title' |
  'description' |
  'discussionQuestions' |
  'thumbnailURL' |
  'thumbnailBlurHash' |
  'videoURL' |
  'verses' |
  'timestamp'
>;
